import { WorkspaceImpl } from '@affine/core/modules/workspace/impls/workspace';
import { clipboardConfigs } from '@blocksuite/affine/foundation/clipboard';
import { defaultImageProxyMiddleware } from '@blocksuite/affine/shared/adapters';
import { replaceSelectedTextWithBlocksCommand } from '@blocksuite/affine/shared/commands';
import { isInsideEdgelessEditor } from '@blocksuite/affine/shared/utils';
import {
  type BlockComponent,
  BlockSelection,
  BlockStdScope,
  Clipboard,
  type EditorHost,
  SurfaceSelection,
  type TextSelection,
} from '@blocksuite/affine/std';
import {
  type BlockModel,
  type BlockSnapshot,
  Slice,
  type Store,
} from '@blocksuite/affine/store';
import { Doc as YDoc } from 'yjs';

import {
  insertFromMarkdown,
  markDownToDoc,
  markdownToSnapshot,
  // replaceFromMarkdown,
} from '../../utils';
import type { AffineAIPanelWidget } from '../widgets/ai-panel/ai-panel';

const getNoteId = (blockElement: BlockComponent) => {
  let element = blockElement;
  while (element.flavour !== 'affine:note') {
    if (!element.parentComponent) {
      break;
    }
    element = element.parentComponent;
  }

  return element.model.id;
};

const setBlockSelection = (
  host: EditorHost,
  parent: BlockComponent,
  models: BlockModel[]
) => {
  const selections = models
    .map(model => model.id)
    .map(blockId => host.selection.create(BlockSelection, { blockId }));

  if (isInsideEdgelessEditor(host)) {
    const surfaceElementId = getNoteId(parent);
    const surfaceSelection = host.selection.create(
      SurfaceSelection,
      selections[0].blockId,
      [surfaceElementId],
      true
    );

    selections.push(surfaceSelection);
    host.selection.set(selections);
  } else {
    host.selection.setGroup('note', selections);
  }
};

export const insert = async (
  host: EditorHost,
  content: string,
  selectBlock: BlockComponent,
  below: boolean = true
) => {
  const blockParent = selectBlock.parentComponent;
  if (!blockParent) return;
  const index = blockParent.model.children.findIndex(
    model => model.id === selectBlock.model.id
  );
  const insertIndex = below ? index + 1 : index;

  const { store } = host;
  const models = await insertFromMarkdown(
    host,
    content,
    store,
    blockParent.model.id,
    insertIndex
  );
  await host.updateComplete;
  requestAnimationFrame(() => setBlockSelection(host, blockParent, models));
};

export const insertBelow = async (
  host: EditorHost,
  content: string,
  selectBlock: BlockComponent
) => {
  await insert(host, content, selectBlock, true);
};

export const insertAbove = async (
  host: EditorHost,
  content: string,
  selectBlock: BlockComponent
) => {
  await insert(host, content, selectBlock, false);
};

export const replace = async (
  host: EditorHost,
  content: string,
  firstBlock: BlockComponent,
  selectedModels: BlockModel[],
  textSelection?: TextSelection
) => {
  const firstBlockParent = firstBlock.parentComponent;
  if (!firstBlockParent) return;
  const firstIndex = firstBlockParent.model.children.findIndex(
    model => model.id === firstBlock.model.id
  );

  if (textSelection) {
    const collection = new WorkspaceImpl({
      id: 'AI_REPLACE',
      rootDoc: new YDoc({ guid: 'AI_REPLACE' }),
    });
    collection.meta.initialize();
    const fragmentDoc = collection.createDoc();

    try {
      const fragment = fragmentDoc.getStore();
      fragmentDoc.load();

      const rootId = fragment.addBlock('affine:page');
      fragment.addBlock('affine:surface', {}, rootId);
      const noteId = fragment.addBlock('affine:note', {}, rootId);

      const { snapshot, transformer } = await markdownToSnapshot(
        content,
        fragment,
        host
      );

      if (snapshot) {
        const blockSnapshots = (
          snapshot.content[0].flavour === 'affine:note'
            ? snapshot.content[0].children
            : snapshot.content
        ) as BlockSnapshot[];

        const blocks = (
          await Promise.all(
            blockSnapshots.map(async blockSnapshot => {
              return await transformer.snapshotToBlock(
                blockSnapshot,
                fragment,
                noteId,
                0
              );
            })
          )
        ).filter(block => block) as BlockModel[];
        host.std.command.exec(replaceSelectedTextWithBlocksCommand, {
          textSelection,
          blocks,
        });
      }
    } finally {
      collection.dispose();
    }
  } else {
    selectedModels.forEach(model => {
      host.store.deleteBlock(model);
    });

    const { store } = host;
    const models = await insertFromMarkdown(
      host,
      content,
      store,
      firstBlockParent.model.id,
      firstIndex
    );

    await host.updateComplete;
    requestAnimationFrame(() =>
      setBlockSelection(host, firstBlockParent, models)
    );
  }
};

export const copyTextAnswer = async (panel: AffineAIPanelWidget) => {
  if (!panel.answer) {
    return false;
  }
  return copyText(panel.answer);
};

export const copyText = async (text: string) => {
  const previewDoc = await markDownToDoc(text, [defaultImageProxyMiddleware]);
  const models = previewDoc
    .getBlocksByFlavour('affine:note')
    .map(b => b.model)
    .flatMap(model => model.children);
  const slice = Slice.fromModels(previewDoc, models);
  const std = new BlockStdScope({
    store: previewDoc,
    extensions: [...clipboardConfigs],
  });
  const clipboard = std.provider.get(Clipboard);
  await clipboard.copySlice(slice);
  previewDoc.dispose();
  return true;
};

export const mdStrTransBlocks = async (
  host: EditorHost,
  doc: Store,
  params: {
    new_str: string;
    selection_str: string;
  }
) => {
  const previewDoc = await markDownToDoc(params.selection_str, [
    defaultImageProxyMiddleware,
  ]);
  const models = previewDoc
    .getBlocksByFlavour('affine:note')
    .map(b => b.model)
    .flatMap(model => model.children);

  //
  const slice = Slice.fromModels(previewDoc, models);

  console.log('####-slice', slice.data.content);

  function getAllBlocks(model: Store) {
    const result: BlockModel[] = [];
    function traverse(block: BlockModel) {
      result.push(block);
      if (block.children && block.children.length > 0) {
        block.children.forEach(traverse);
      }
    }
    if (model.root) {
      traverse(model.root);
    }
    return result;
  }

  // 当前文档中的所有 block
  const allBlockIds = getAllBlocks(doc);

  function isBlockContentEqual<T extends BlockModel<{ text?: object }>>(
    a: T,
    b: T
  ) {
    // 只比对核心内容字段，忽略 id
    // 这里以 paragraph 为例，复杂 block 需递归 props/children
    if (a.flavour !== b.flavour) return false;
    if (a.props?.text && b.props?.text) {
      console.log('#####a.props.text', JSON.stringify(a.props.text));
      console.log('#####b.props.text', JSON.stringify(b.props.text));

      return JSON.stringify(a.props.text) === JSON.stringify(b.props.text);
    }
    // 其他类型可扩展
    return JSON.stringify(a.props) === JSON.stringify(b.props);
  }

  function findMatchingBlockRange<T extends BlockModel<{ text?: object }>>(
    editorBlocks: T[],
    targetBlocks: T[]
  ) {
    for (let i = 0; i <= editorBlocks.length - targetBlocks.length; i++) {
      let match = true;
      for (let j = 0; j < targetBlocks.length; j++) {
        if (!isBlockContentEqual(editorBlocks[i + j], targetBlocks[j])) {
          match = false;
          break;
        }
      }
      if (match) {
        return editorBlocks.slice(i, i + targetBlocks.length);
      }
    }
    return [];
  }

  const sliceBlocks = slice.data.content as unknown as BlockModel<{
    text?: object;
  }>[];

  // 遍历 映射到需要在选中的 blocks
  const matchedBlocks = findMatchingBlockRange(allBlockIds, sliceBlocks);
  const blockIds = matchedBlocks.map(b => b.id);

  console.log('####selection-parse', blockIds);
  console.log(
    '####all',
    allBlockIds.map(a => a.id)
  );

  // const selections = blockIds.map(blockId => host.selection.create(BlockSelection, { blockId }));

  // // replace()

  // const selections = createTextSelectionFromBlocks(matchedBlocks, host.std);

  // return slice;

  const collection = new WorkspaceImpl({
    id: 'AI_REPLACE',
    rootDoc: new YDoc({ guid: 'AI_REPLACE' }),
  });
  collection.meta.initialize();
  const fragmentDoc = collection.createDoc();

  const fragment = fragmentDoc.getStore();
  fragmentDoc.load();

  const rootId = fragment.addBlock('affine:page');
  fragment.addBlock('affine:surface', {}, rootId);
  const noteId = fragment.addBlock('affine:note', {}, rootId);

  try {
    const { snapshot, transformer } = await markdownToSnapshot(
      params.new_str,
      fragment,
      host
    );

    if (snapshot) {
      // const snapshots = snapshot?.content.flatMap(x => x.children) ?? [];

      const blockSnapshots = (
        snapshot.content[0].flavour === 'affine:note'
          ? snapshot.content[0].children
          : snapshot.content
      ) as BlockSnapshot[];

      const blocks = (
        await Promise.all(
          blockSnapshots.map(async blockSnapshot => {
            return await transformer.snapshotToBlock(
              blockSnapshot,
              fragment,
              noteId,
              0
            );
          })
        )
      ).filter(block => block);

      // 写入覆盖
      console.log('####-old_blocks', blocks, matchedBlocks);

      return {
        targetBlocks: matchedBlocks,
        newBlocks: blockSnapshots,
      };

      // 写入覆盖
      // if (selections) {
      //   host.std.command.exec(replaceSelectedTextWithBlocksCommand, {
      //     textSelection: selections,
      //     blocks: blocks,
      //   });
      // }

      // return blocks;

      // host.std.command.exec(replaceSelectedTextWithBlocksCommand, {
      //   textSelection,
      //   blocks,
      // });
    }
  } finally {
    collection.dispose();
  }

  // const res = await replaceFromMarkdown(host, params.new_str, doc);
};

// function createTextSelectionFromBlocks(
//   blocks: BlockModel[],
//   std: BlockStdScope
// ): TextSelection | null {
//   if (blocks.length === 0) return null;

//   // 如果只有一个 block，选择整个 block 的内容
//   if (blocks.length === 1) {
//     const block = blocks[0];
//     if (!block.text) return null;

//     return std.selection.create(TextSelection, {
//       from: {
//         blockId: block.id,
//         index: 0,
//         length: block.text.length,
//       },
//       to: null,
//     });
//   }

//   // 如果多个 block，需要跨 block 选择
//   const firstBlock = blocks[0];
//   const lastBlock = blocks[blocks.length - 1];

//   if (!firstBlock.text || !lastBlock.text) return null;

//   return std.selection.create(TextSelection, {
//     from: {
//       blockId: firstBlock.id,
//       index: 0,
//       length: firstBlock.text.length, // 选择整个第一个 block
//     },
//     to: {
//       blockId: lastBlock.id,
//       index: 0,
//       length: lastBlock.text.length, // 选择整个最后一个 block
//     },
//   });
// }
